import { Suspense, useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { useChartTheme } from '@/lib/charts/use-chart-theme';
import { cn } from '@/lib/utils';
import { EscenaDeMisionDiferida } from '../animaciones/cargar-escena';
import { logoDePrixia } from '../animaciones/elegir-logo';
import { duracionDelPanel } from '../animaciones/piezas-del-logo';
import { MARGEN_DE_SEGURIDAD_MS, PLAZO_DE_CARGA_MS } from '../animaciones/tiempos-de-apertura';

/**
 * El panel flotante de una misión cumplida (spec 344). Se ve recién con la escena lista,
 * no recibe clics y se cierra solo: si el 3D no llega a tiempo, falla o la pestaña está
 * oculta, se va sin dejar rastro y la tarjeta sigue con su logo fijo.
 */
export function PanelDeMision({
  enTotal,
  abierta,
  alCerrar,
}: {
  enTotal: number;
  /** Con la tarjeta abierta va a su lado: arriba no entra, y encima taparía sus botones. */
  abierta: boolean;
  alCerrar: () => void;
}) {
  const { oscuro } = useChartTheme();
  // Se fija al abrir: si cambiara, la escena se rehace y la animación vuelve a empezar.
  const [fondo] = useState<'claro' | 'oscuro'>(oscuro ? 'oscuro' : 'claro');
  const [lista, setLista] = useState(false);
  const cerrar = useRef(alCerrar);
  cerrar.current = alCerrar;

  useEffect(() => {
    const plazo = lista ? duracionDelPanel(enTotal) + MARGEN_DE_SEGURIDAD_MS : PLAZO_DE_CARGA_MS;
    const id = window.setTimeout(() => cerrar.current(), plazo);
    return () => window.clearTimeout(id);
  }, [lista, enTotal]);

  return (
    <motion.div
      data-panel-de-mision=""
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute h-[240px] w-full overflow-hidden rounded-2xl border bg-card shadow-xl md:w-[300px]',
        // Teléfono: siempre encima de la barra (a la derecha no hay lugar); con la hoja
        // abierta, arriba de todo.
        abierta
          ? 'max-md:fixed max-md:inset-x-3 max-md:top-3 max-md:w-auto md:bottom-0 md:left-full md:ml-3'
          : 'bottom-full left-0 mb-3'
      )}
      initial={{ opacity: 0 }}
      animate={{ opacity: lista ? 1 : 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <Suspense fallback={null}>
        <EscenaDeMisionDiferida
          logo={logoDePrixia()}
          enTotal={enTotal}
          fondo={fondo}
          alListo={() => setLista(true)}
          alTerminar={() => cerrar.current()}
          alFallar={() => cerrar.current()}
        />
      </Suspense>
    </motion.div>
  );
}
